import React from 'react';
import { NumericFormat } from 'react-number-format';
import { jwtDecode } from 'jwt-decode';
import moment from 'moment-timezone';

export const isDate = v => {
  // Create a new Date object from the string
  const date = new Date(v);

  // Check if the resulting object is valid
  return !isNaN(date.getTime());
};

export const modalOptionsWrapper = (v, fldValue, labelValue) => {
  let options = [];
  options = v.map(d => ({ label: d[labelValue], value: d[fldValue] }));
  return options;
};

export const convertToTitleCase = str => {
  return str.replace(/\w\S*/g, txt => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

// import not working from @constants so this was here.
const knownBooleanFields = ['pealert', 'peambulimpleft', 'peambulimpright', 'pecoopex', 'pedist', 'peovrw', 'pesobpain', 'pewd', 'peweak', 'pewn'];

export const shuffle = arr => {
  let currentIndex = arr.length;
  let temporaryValue;
  let randomIndex;

  // While there remain elements to shuffle...
  while (currentIndex !== 0) {
    // Pick a remaining element...
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;

    // And swap it with the current element.
    temporaryValue = arr[currentIndex];
    arr[currentIndex] = arr[randomIndex];
    arr[randomIndex] = temporaryValue;
  }

  return arr;
};

export const checkRecordExisting = (arr, fld, v) => {
  return arr.find(r => r[fld] === v);
};

export const useDebounce = (func, wait) => {
  let timeout;
  return (...args) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      func.apply(this, args);
    }, wait);
  };
};

/**
 * decode jwt
 * @param {*} params
 */
export const decode = params => {
  return jwtDecode(params);
};

export const checkToken = v => {
  // console.log('[checktoken] ', (v.exp * 1000) < Date.now())
  return v.exp * 1000 < Date.now();
};

/**
 * anti boolean 'true'
 * @param {*} params
 */
export const evalBoolean = params => {
  return params === 'true' || params === true;
};

export const trimSelectionItems = v => v.map(i => i.trim());

export const trimArrayFields = v => {
  const evalObj = v;
  Object.keys(evalObj).map(fld => {
    if (Array.isArray(evalObj[fld])) {
      evalObj[fld] = trimSelectionItems(evalObj[fld]);
    }
    return fld;
  });
  return evalObj;
};

export const evaluateBooleanFields = v => {
  const evalObj = v;
  Object.keys(evalObj).map(fld => {
    if (evalObj[fld] === 'true' || evalObj[fld] === 'false') {
      evalObj[fld] = evalBoolean(evalObj[fld]);
    }

    if (knownBooleanFields.includes(evalObj[fld])) {
      evalObj[fld] = evalObj[fld] === '' ? false : true;
    }

    return fld;
  });
  return evalObj;
};

export const evaluateBooleanFieldsArray = v => {
  const finalArr = [];
  v.map(x => {
    const evalObj = x;
    Object.keys(evalObj).map(fld => {
      if (evalObj[fld] === 'true' || evalObj[fld] === 'false') {
        evalObj[fld] = evalBoolean(evalObj[fld]);
      }
      return fld;
    });
    finalArr.push(evalObj);
    return x;
  });
  return finalArr;
};

/**
 * convert object to query string
 * @param {*} params
 */
export const convertQueryString = params => {
  return Object.keys(params)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');
};

/**
 * convert moment
 * @param {*} params
 */
export const convertMomentWithFormat = (v, fmt = 'MM/DD/YYYY') => {
  // console.log('[convertMomentWithFormat] ', v);
  // undefined will return date today
  if (v !== '') {
    moment.tz.guess(true);
    return moment.utc(v).format(fmt);
  }
  return '';
};

/**
 * Safe value for Ant Design DatePicker (moment instance or null — never '').
 */
export const toMomentOrNull = v => {
  if (v === null || v === undefined || v === '') {
    return null;
  }
  if (moment.isMoment(v)) {
    return v.isValid() ? v : null;
  }
  const m = moment(v);
  return m.isValid() ? m : null;
};

/**
 * @deprecated Prefer toMomentOrNull — kept for call-site compatibility.
 */
export const convertToMomentObj = v => toMomentOrNull(v);

/** DatePicker field names on patient demographics forms */
const PATIENT_DATE_FIELDS = ['dob', 'doe', 'fdl'];

/** DatePicker field names on work status detail form */
const WORKSTATUS_DATE_FIELDS = ['ws4', 'ws5', 'ws7', 'ws8', 'ws9', 'ws10', 'ws37', 'ws47', 'fullDutyDate'];

export const normalizeWorkstatusDatesForForm = values => {
  if (!values || typeof values !== 'object') {
    return values;
  }
  const next = { ...values };
  WORKSTATUS_DATE_FIELDS.forEach(field => {
    if (field in next) {
      next[field] = toMomentOrNull(next[field]);
    }
  });
  if ('ws2' in next && next.ws2 !== '' && next.ws2 != null) {
    if (isDate(next.ws2)) {
      next.ws2 = toMomentOrNull(next.ws2);
    }
  }
  return next;
};

export const normalizePatientDatesForForm = values => {
  if (!values || typeof values !== 'object') {
    return values;
  }
  const next = { ...values };
  PATIENT_DATE_FIELDS.forEach(field => {
    if (field in next) {
      next[field] = toMomentOrNull(next[field]);
    }
  });
  return next;
};

export const formatPriceX = (price, key = '') => {
  const formattedPrice = parseFloat(price)
    .toFixed(2)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return key === '' ? `$ ${formattedPrice}` : `${key}$ ${formattedPrice}`;
};

/**
 * format price, fot example:
 * from 20, to $20.00
 * @param {*} price
 */
export const formatPrice = price => <NumericFormat decimalScale={2} fixedDecimalScale value={price} displayType="text" thousandSeparator prefix="$" />;

export { clearWorkspaceStorage as clearSessionStorage, clearPatientWorkspace } from './workspaceStorage';

export const capitalize = s => {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

/**
 * generate random string
 */
export const getRandomString = () => {
  const r = Math.random().toString(36).substring(7);
  return r;
};

export const parseDateField = v => toMomentOrNull(v);

export const normalizeStringToBreakTag = value => {
  return value?.replace(/(?:\r\n|\r|\n)/g, '<br>')?.replace(/<br>/g, '<br/>');
};

const stringSorter = (a, b) => {
  return a?.localeCompare(b);
};

export const tableStringSorter = (a, b, { valuePropsName }) => {
  if (!valuePropsName) {
    return stringSorter(a, b);
  }
  return stringSorter(a[valuePropsName], b[valuePropsName]);
};

export const getDirtyFields = (x, v) => {
  let result = {};
  Object.keys(x).forEach(d => {
    if (d === 'pname' || x[d] === v[d]) return;
    // a field missing from the form must never be sent, and a blank DOB must not wipe a stored one
    if (v[d] === undefined || (d === 'dob' && v[d] === '')) return;

    if (d === 'lname' || d === 'fname' || d === 'mname') {
      const fname = v.fname ?? x.fname ?? '';
      const mname = v.mname ?? x.mname ?? '';
      const lname = v.lname ?? x.lname ?? '';
      result.pname = [fname, mname, lname].filter(Boolean).join(' ');
      result.fname = fname;
      result.mname = mname;
      result.lname = lname;
    } else {
      result[d] = v[d];
    }
  });
  return result;
};

/**
 * Convert date to plain string MM/DD/YYYY
 * @param {*} v - date string or object
 */
export const formatToPlainDateString = v => {
  if (!v) return '';
  const m = moment.isMoment(v) ? v : moment(v);
  return m.isValid() ? m.format('MM/DD/YYYY') : '';
};
